/**
 * Individuelle Farbschwellen des IDS-Prüfberichts (#2309).
 *
 * <h2>Warum das nötig ist</h2>
 * Der IDS-Bericht war bis hierher **binär**: der Status einer Spezifikation kommt vom Prüfmodul
 * als Boolean (`spec_result === 1.0`), alles unter 100 % war damit rot. Ein Ergebnis von 92 % sah
 * aus wie eines von 3 %. Wie streng gefärbt wird, ist aber eine Projektentscheidung — deshalb sind
 * die Stufen pro Prüfregelset konfigurierbar.
 *
 * <h2>Rückwärtskompatibilität</h2>
 * Ist nichts konfiguriert, wird {@link parseIdsThresholds} `null` und der Bericht färbt **exakt
 * wie bisher**. Es gibt bewusst keinen „Default 50/90", der stillschweigend greift: das würde
 * jeden Bestandsbericht umfärben.
 */

import {NormalizedReport} from './normalized-report.model';

/** Statuswerte, die der Bericht kennt. */
type ReportStatus = 'success' | 'warning' | 'error' | 'unknown';

/**
 * Untergrenzen der beiden oberen Stufen, jeweils in Prozent und einschließlich.
 *
 * Bewusst als „ab wann" modelliert und nicht als „bis wann": das Ticket beschreibt die Stufen als
 * `Error 0-50 / Warnung 50-90 / Erfolg 90-100`, und an den Grenzen ist nur so eindeutig, wohin
 * genau 50 bzw. 90 gehören.
 */
export interface IdsThresholds {
  /** Ab diesem Prozentwert gilt ein Ergebnis als Warnung (z. B. 50). */
  warningFrom: number;
  /** Ab diesem Prozentwert gilt ein Ergebnis als erfüllt (z. B. 90). */
  successFrom: number;
}

/**
 * Vorbelegung, wenn ein Nutzer die eigene Farbcodierung erstmals einschaltet. Nicht der Default
 * für die Anzeige: ohne Konfiguration wird gar nicht umgefärbt.
 */
export const IDS_THRESHOLDS_SUGGESTION: IdsThresholds = {warningFrom: 50, successFrom: 100};

/**
 * Stufen für das **Gesamtergebnis** (#2317), wenn am Prüfregelset nichts konfiguriert ist.
 *
 * Hier ist ein Default richtig, anders als bei den Zeilenfarben: der Gesamtkreis ist neu — vorher
 * standen dort drei Verteilungskreise, von denen der grüne unabhängig vom Wert immer grün war. Es
 * gibt also kein bisheriges Verhalten, das sich ändern könnte. Die Werte entsprechen der
 * app-weiten Schwelle (`statusFromPercent`) und dem Beispiel im Ticket: 50 % ist eine Warnung.
 */
export const DEFAULT_OVERALL_THRESHOLDS: IdsThresholds = {warningFrom: 50, successFrom: 100};

/** Schlüssel in `CheckRule.configJson`, unter dem die Konfiguration des Berichts liegt. */
const CONFIG_KEY = 'idsReport';

/** Status eines Prozentwerts anhand der konfigurierten Stufen. */
export function statusFromIdsThresholds(
  percent: number, thresholds: IdsThresholds,
): 'success' | 'warning' | 'error' {
  if (percent >= thresholds.successFrom) return 'success';
  if (percent >= thresholds.warningFrom) return 'warning';
  return 'error';
}

/**
 * Prüft und sortiert ein Schwellwertpaar.
 *
 * Gibt `null` für alles zurück, was keine brauchbare Stufung ergibt — statt die Werte
 * stillschweigend zu korrigieren und dann anders zu färben, als im Editor steht. Eine vertauschte
 * Eingabe (`successFrom < warningFrom`) wird allerdings getauscht: sie ist eindeutig gemeint und
 * entsteht leicht beim Tippen.
 */
export function normalizeIdsThresholds(raw: unknown): IdsThresholds | null {
  if (!raw || typeof raw !== 'object') return null;
  const warningFrom = toPercent((raw as any).warningFrom);
  const successFrom = toPercent((raw as any).successFrom);
  if (warningFrom == null || successFrom == null) return null;
  return warningFrom <= successFrom
    ? {warningFrom, successFrom}
    : {warningFrom: successFrom, successFrom: warningFrom};
}

/** Liest die Schwellwerte aus dem `configJson` eines Prüfregelsets. */
export function parseIdsThresholds(configJson: string | null | undefined): IdsThresholds | null {
  if (!configJson?.trim()) return null;
  try {
    const config = JSON.parse(configJson);
    return normalizeIdsThresholds(config?.[CONFIG_KEY]?.thresholds);
  } catch {
    // Unlesbares configJson darf den Bericht nicht kosten – dann färbt er wie bisher.
    return null;
  }
}

/**
 * Schreibt die Schwellwerte in ein bestehendes `configJson` zurück, ohne fremde Schlüssel zu
 * verlieren.
 *
 * `null` entfernt die Konfiguration. Das Ergebnis ist immer ein JSON-String und nie `null`, weil
 * das Backend `null` als „unverändert" liest — ein Abwählen käme so nie an.
 */
export function writeIdsThresholds(
  configJson: string | null | undefined, thresholds: IdsThresholds | null,
): string {
  let config: Record<string, any> = {};
  if (configJson?.trim()) {
    try {
      const parsed = JSON.parse(configJson);
      if (parsed && typeof parsed === 'object') config = parsed;
    } catch {
      // Unlesbares configJson wird ersetzt statt den Speichern-Vorgang zu verweigern.
    }
  }

  const section = {...(config[CONFIG_KEY] ?? {})};
  if (thresholds) {
    section.thresholds = thresholds;
  } else {
    delete section.thresholds;
  }

  if (Object.keys(section).length > 0) config[CONFIG_KEY] = section;
  else delete config[CONFIG_KEY];

  return JSON.stringify(config);
}

/**
 * Färbt einen fertig geparsten IDS-Bericht nach den konfigurierten Stufen um.
 *
 * <p>Bewusst als Nachbearbeitung und nicht im Adapter: die Schwellwerte hängen am Prüfregelset und
 * werden asynchron nachgeladen, der Bericht ist zu diesem Zeitpunkt längst geparst. Dasselbe
 * Muster nutzt der Bericht schon für Regelkürzel und Beschreibungen
 * (`CheckReportPageComponent.applyRuleMetadataToReport`) — inklusive des dortigen Hinweises, dass
 * die abgeleiteten Caches danach verworfen werden müssen, weil die Objektidentität gleich bleibt.
 *
 * <p>Zwei Dinge werden **nicht** angefasst:
 * <ul>
 *   <li>`unknown` — ohne bewertetes Ergebnis (`percent === null`) gibt es nichts einzufärben.</li>
 *   <li>ein bereits gesetztes `warning` — das stammt aus der Fehlerindikation (#2308, eine
 *       Anforderung scheitert nur am Datentyp) und trägt mehr Information als die reine
 *       Prozentstufe. Es wird deshalb nicht durch die Schwellwerte überschrieben.</li>
 * </ul>
 */
export function applyIdsThresholdsToReport(
  report: NormalizedReport | null | undefined, thresholds: IdsThresholds,
): void {
  if (!report) return;

  const recolor = (percent: number | null | undefined, current: ReportStatus): ReportStatus =>
    percent == null || current === 'unknown' || current === 'warning'
      ? current
      : statusFromIdsThresholds(percent, thresholds);

  for (const group of report.groups ?? []) {
    group.status = recolor(group.percent, group.status);
    for (const rule of group.rules ?? []) {
      rule.status = recolor(rule.percent, rule.status);
      for (const detail of rule.details ?? []) {
        detail.type = recolor(detail.percent, detail.type);
      }
    }
  }
}

/** Prozentwert 0–100 oder `null`, wenn der Wert keiner ist. */
function toPercent(value: unknown): number | null {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 0 || numeric > 100) return null;
  return numeric;
}
